import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { QrCode, Loader2 } from "lucide-react";

export const AuthShell = ({ title, subtitle, children }) => (
  <div className="min-h-screen bg-background flex items-center justify-center px-4 py-10">
    <div className="w-full max-w-md">
      <Link to="/" className="flex items-center gap-2.5 justify-center mb-8">
        <div className="grid h-10 w-10 place-items-center rounded-xl bg-accent text-accent-foreground">
          <QrCode className="h-5 w-5" strokeWidth={2} />
        </div>
        <span className="font-display text-2xl font-extrabold tracking-tighter">DynaQR</span>
      </Link>
      <div className="rounded-2xl border border-border bg-card p-7">
        <h1 className="font-display text-2xl font-extrabold tracking-tighter">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
        <div className="mt-6">{children}</div>
      </div>
    </div>
  </div>
);

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "admin" ? "/admin" : "/app");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    }
    setLoading(false);
  };

  return (
    <AuthShell title="Welcome back" subtitle="Log in to your DynaQR account">
      <form onSubmit={submit} className="space-y-4">
        {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link to="/forgot-password" className="text-xs text-accent hover:underline">Forgot?</Link>
          </div>
          <Input id="password" type="password" data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        </div>
        <Button type="submit" disabled={loading} className="w-full rounded-full" data-testid="login-submit">
          {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Log in
        </Button>
      </form>
      <p className="mt-5 text-center text-sm text-muted-foreground">
        New to DynaQR? <Link to="/signup" className="text-accent hover:underline font-medium">Create an account</Link>
      </p>
    </AuthShell>
  );
}
